import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, Users, TrendingUp, CheckSquare, Calendar, 
  Bell, FileText, DollarSign, UserPlus, BarChart3, Upload, 
  Brain, Crown, ChevronLeft, Building2, MapPin
} from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function SalesLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem('userData') || '{}');
  const salesUser = JSON.parse(localStorage.getItem('salesUser') || '{}');
  
  // Use salesUser if available, otherwise fallback to main user
  // For unified users, use sales_role from userData
  const currentUser = salesUser.id ? salesUser : {
    ...user,
    role: user.sales_role || user.role,  // Use sales_role if available
    is_platform_admin: user.role === 'super_admin'
  };

  const navItems = [
    { path: '/suite/sales', icon: LayoutDashboard, label: 'Dashboard', exact: true },
    { path: '/suite/sales/ai', icon: Brain, label: 'AI Command Center' },
    { path: '/suite/sales/contacts', icon: Users, label: 'Contacts' },
    { path: '/suite/sales/leads', icon: TrendingUp, label: 'Leads' },
    { path: '/suite/sales/contracts', icon: FileText, label: 'Contracts' },
    { path: '/suite/sales/tasks', icon: CheckSquare, label: 'Tasks' },
    { path: '/suite/sales/interviews', icon: Calendar, label: 'Interviews' },
    { path: '/suite/sales/onboarding', icon: UserPlus, label: 'Candidate Onboarding' },
    { path: '/suite/sales/reminders', icon: Bell, label: 'Reminders' },
  ];

  // C-Level roles
  const cLevelRoles = ['ceo', 'cso', 'director'];
  const isCLevel = cLevelRoles.includes(currentUser.role);
  const isManager = currentUser.role === 'manager';
  const isAdmin = currentUser.role === 'admin' || currentUser.role === 'super_admin' || user.role === 'super_admin';

  // Add manager/admin/c-level routes
  if (isManager || isAdmin || isCLevel) {
    navItems.push({ path: '/suite/sales/revenue', icon: DollarSign, label: 'Revenue' });
    navItems.push({ path: '/suite/sales/reports', icon: BarChart3, label: 'Reports' });
  }

  if (isAdmin || isCLevel) {
    navItems.push({ path: '/suite/sales/team', icon: UserPlus, label: 'Team' });
    navItems.push({ path: '/suite/sales/team/locate', icon: MapPin, label: 'Locate Team' });
    navItems.push({ path: '/suite/sales/import', icon: Upload, label: 'Import Data' });
  }

  if (currentUser.is_platform_admin || isCLevel) {
    navItems.push({ path: '/suite/sales/platform-admin', icon: Crown, label: 'Platform Admin' });
  }

  return (
    <div className="flex h-screen bg-slate-950">
      {/* Sidebar */}
      <div className="w-64 bg-slate-900 text-slate-300 flex flex-col border-r border-slate-800">
        {/* Header with back to portal */}
        <div className="p-4 border-b border-slate-800">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/portal')}
            className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-800 mb-3"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            All Products
          </Button>
          <div className="flex items-center gap-3 px-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-semibold text-white">MySales Orbit</h2>
              <p className="text-xs text-slate-400">Contract Staffing CRM</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto" data-testid="sales-sidebar-nav">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.exact}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                  isActive
                    ? 'bg-orange-600 text-white'
                    : 'hover:bg-slate-800 hover:text-white'
                }`
              }
              data-testid={`sales-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <item.icon className="w-5 h-5" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* User Info */}
        <div className="p-4 border-t border-slate-800">
          <div className="px-2">
            <p className="text-sm font-medium text-white" data-testid="sales-user-name">
              {currentUser.name || user.name || 'User'}
            </p>
            <p className="text-xs text-slate-400" data-testid="sales-user-role">
              {currentUser.title || currentUser.role || 'Sales User'}
            </p>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 overflow-auto bg-slate-950">
        <Outlet />
      </div>
    </div>
  );
}
